import type { EventTickets, TicketSlot, TicketSlotKind } from './firestore'
import type { PregameAudience } from './pregames'

// The saveEventTickets / respondToTicket callables' contract, shared so the
// editor and the server validate the same way.

export const TICKETS_MAX = 10
export const TICKET_NAME_MAX = 40

export const eventTicketsId = (userId: string, eventId: string) => `${userId}_${eventId}`

export interface TicketSlotInput {
  id?: string
  kind: TicketSlotKind
  userId?: string
  name?: string
}

export interface SaveEventTicketsRequest extends PregameAudience {
  eventId: string
  /** Absent = keep the slots read in the transaction. */
  tickets?: TicketSlotInput[]
  /** Absent = keep. */
  seeking?: boolean
  /** The version the caller edited from; absent = no check. */
  version?: number
  /**
   * true = replace the stored audience with audienceGroupIds/audienceFriends
   * (both absent = whole crew). Also implied when either audience field is
   * present; otherwise the stored audience is kept.
   */
  setAudience?: boolean
}

export interface SaveEventTicketsResult {
  saved: boolean
  created: boolean
  deleted: boolean
  version: number
}

export type TicketResponse = 'claim' | 'accept' | 'decline' | 'give_back'
export const TICKET_RESPONSES: readonly TicketResponse[] = ['claim', 'accept', 'decline', 'give_back']

export interface RespondToTicketRequest {
  ledgerId: string
  slotId: string
  action: TicketResponse
}

export interface RespondToTicketResult {
  ok: true
}

export function openSlots(l: Pick<EventTickets, 'tickets'>): TicketSlot[] {
  return (l.tickets ?? []).filter(s => s.kind === 'open')
}

/** The slot naming `uid` — a 'user' slot, pending or accepted. */
export function slotFor(l: Pick<EventTickets, 'tickets'>, uid: string): TicketSlot | undefined {
  return (l.tickets ?? []).find(s => s.kind === 'user' && s.userId === uid)
}

export function hasMeSlot(l: Pick<EventTickets, 'tickets'> | null | undefined): boolean {
  return !!l && (l.tickets ?? []).some(s => s.kind === 'me')
}

/**
 * The denormalized uid lists a ledger stores: who holds an accepted ticket
 * (the owner via a 'me' slot), who still has to answer, and every Faro user
 * a slot names.
 */
export function ledgerDerived(ownerId: string, tickets: TicketSlot[]): {
  holderIds: string[]
  pendingIds: string[]
  assignedIds: string[]
} {
  const holders = new Set<string>()
  const pending = new Set<string>()
  for (const s of tickets) {
    if (s.kind === 'me') holders.add(ownerId)
    else if (s.kind === 'user' && s.userId) (s.accepted ? holders : pending).add(s.userId)
  }
  const assigned = tickets.flatMap(s => (s.kind === 'user' && s.userId ? [s.userId] : []))
  return { holderIds: [...holders].sort(), pendingIds: [...pending].sort(), assignedIds: assigned }
}

/** Who can read a ledger: the expanded audience plus every Faro user a slot names, minus the owner and people they blocked. */
export function ledgerViewerIds(ownerId: string, audienceCrew: string[], tickets: TicketSlot[], ownerBlockedIds: string[]): string[] {
  const ids = new Set([...audienceCrew, ...ledgerDerived(ownerId, tickets).assignedIds])
  ids.delete(ownerId)
  for (const id of ownerBlockedIds) ids.delete(id)
  return [...ids].sort()
}
